import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaTrash, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendar, FaEuroSign, FaMicrophone, FaUsers, FaClock } from 'react-icons/fa';
import axios from 'axios';
import '../styles/BookingDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function LivemusikBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBooking();
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/livemusik/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooking(response.data.booking);
    } catch (error) {
      console.error('Error loading Livemusik booking:', error);
      alert('Fehler beim Laden der Livemusik-Buchung');
      navigate('/livemusik');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Livemusik-Buchung wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/livemusik/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/livemusik');
    } catch (error) {
      alert('Fehler beim Löschen: ' + error.message);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/livemusik/${id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setBooking({ ...booking, Buchungsstatus: newStatus });
    } catch (error) {
      alert('Fehler beim Aktualisieren: ' + error.message);
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Neu': 'neu',
      'In Bearbeitung': 'angefragt',
      'Angebot erstellt': 'angefragt',
      'Bestätigt': 'bestaetigt',
      'Abgeschlossen': 'abgeschlossen',
      'Abgelehnt': 'zurueck'
    };
    return statusMap[status] || 'neu';
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (!booking) {
    return <div className="card">Livemusik-Buchung nicht gefunden</div>;
  }

  return (
    <div className="booking-detail-page">
      <div className="page-header">
        <div>
          <Link to="/livemusik" className="back-button">
            <FaArrowLeft /> Zurück zur Übersicht
          </Link>
          <h1><FaMicrophone /> {booking.Vorname} {booking.Nachname}</h1>
          <span className={`status-badge status-${getStatusColor(booking.Buchungsstatus)}`}>
            {booking.Buchungsstatus || 'Neu'}
          </span>
        </div>
        <div className="header-actions">
          <Link to={`/livemusik/${id}/edit`} className="btn btn-secondary">
            <FaEdit /> Bearbeiten
          </Link>
          <button onClick={handleDelete} className="btn btn-outline" style={{ borderColor: '#dc2626', color: '#dc2626' }}>
            <FaTrash /> Löschen
          </button>
        </div>
      </div>

      {/* Status Workflow */}
      <div className="card mb-3">
        <h2 className="mb-3">Status ändern</h2>
        <div className="status-buttons">
          {['Neu', 'In Bearbeitung', 'Angebot erstellt', 'Bestätigt', 'Abgeschlossen', 'Abgelehnt'].map(status => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`status-button ${booking.Buchungsstatus === status ? 'active' : ''} status-${getStatusColor(status)}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Info Grid */}
      <div className="info-grid">
        {/* Kundendaten */}
        <div className="card info-card">
          <h2>Kundendaten</h2>
          <div className="info-row">
            <FaEnvelope />
            <div>
              <label>E-Mail</label>
              <a href={`mailto:${booking['E-Mail']}`}>{booking['E-Mail']}</a>
            </div>
          </div>
          <div className="info-row">
            <FaPhone />
            <div>
              <label>Telefon</label>
              <a href={`tel:${booking.Telefon}`}>{booking.Telefon}</a>
            </div>
          </div>
        </div>

        {/* Event-Details */}
        <div className="card info-card">
          <h2>Event-Details</h2>
          <div className="info-row">
            <FaCalendar />
            <div>
              <label>Event-Datum</label>
              <p>{booking['Event-Datum'] ? new Date(booking['Event-Datum']).toLocaleDateString('de-DE') : '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaClock />
            <div>
              <label>Startzeit</label>
              <p>{booking.Startzeit || '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaMapMarkerAlt />
            <div>
              <label>Event-Ort</label>
              <p>{booking['Event Ort'] || '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaMicrophone />
            <div>
              <label>Event-Typ</label>
              <p>{booking['Event-Typ']?.[0] || '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaUsers />
            <div>
              <label>Gästeanzahl</label>
              <p>{booking.Gästeanzahl || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Buchungsdetails */}
      <div className="card info-card">
        <h2>Buchungsdetails</h2>
        <div className="info-grid">
          <div className="info-row">
            <FaClock />
            <div>
              <label>Set-Dauer</label>
              <p>{booking['Set-Dauer'] || '-'}</p>
            </div>
          </div>
          <div className="info-row">
            <FaEuroSign />
            <div>
              <label>Preis</label>
              <p className="text-primary">
                <strong>
                  {booking['Manueller Preis aus CRM'] 
                    ? `${booking['Manueller Preis aus CRM']}€` 
                    : (booking.Gesamtpreis ? `${booking.Gesamtpreis}€ (automatisch)` : 'Nicht festgelegt')}
                </strong>
              </p>
            </div>
          </div>
        </div>
        
        {booking.Musikstil && (
          <div className="info-row">
            <FaMicrophone />
            <div>
              <label>Musikstil / Wünsche</label>
              <p>{booking.Musikstil}</p>
            </div>
          </div>
        )}
      </div>

      {/* Notizen */}
      {booking['Event-Notizen'] && (
        <div className="card">
          <h2>Event-Notizen</h2>
          <p className="notes-text">{booking['Event-Notizen']}</p>
        </div>
      )}
    </div>
  );
}

export default LivemusikBookingDetail;
